import { Checkbox } from '@mui/joy';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { StripeError } from '@stripe/stripe-js';

import { useState } from 'react';

import { Button, Tooltip } from '@/design-system';
import {
  useCurrentOrganization,
  useOnPaymentMethodSuccess,
} from '@/hooks/useSwr';
import {
  ChangePaymentPlanTierSchema,
  StripeBusinessInfo,
  StripeCustomerBillingInfo,
  StripePaymentInformation,
} from '@/types/schema';

import { useStripeRedirectUrl } from '../utils/getStripeRedirectUrl';
import { useBusinessInfoSubForm } from '../utils/useBusinessInfoSubForm';
import { BusinessInfoSubForm } from './BusinessInfoSubForm';

export function PaymentSetupElement(props: {
  clientSecret: string;
  onPrev?: () => void;
  onNext?: () => void;
  targetPlanTier: ChangePaymentPlanTierSchema | null;
  loading: boolean;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentComplete, setPaymentComplete] = useState(false);
  const { data: organization } = useCurrentOrganization();
  const businessFormProps = useBusinessInfoSubForm({ showBillingEmail: true });
  const {
    billingInfo,
    taxId,
    taxIdInfo,
    addressComplete,
    setTaxIdError,
    isBusiness,
    setIsBusiness,
    businessName,
    email,
    setEmailError,
  } = businessFormProps;

  const redirectUrl = useStripeRedirectUrl({
    billingInfo,
    isBusiness,
    targetTier: props.targetPlanTier ?? 'undefined',
    taxId: taxId,
    taxIdType: taxIdInfo?.countryEnum,
    businessName,
    invoiceEmail: businessFormProps.email,
  });

  const isPersonal = organization?.is_personal;
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const { trigger: onPaymentMethodSuccess } = useOnPaymentMethodSuccess();

  const handleError = (error: { message?: string }) => {
    setLoading(false);
    setErrorMessage(error.message);
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateEmail = (email: string): boolean => {
    return emailRegex.test(email);
  };

  const handleSetupIntentResult = async (result: {
    setupIntent?: {
      status: string;
      id: string;
    };
    paymentIntent?: {
      status: string;
      id: string;
    };
    error?: StripeError;
  }) => {
    const { error, setupIntent, paymentIntent } = result;
    if (error) {
      handleError(error);
      return;
    }
    const status = setupIntent?.status || paymentIntent?.status;
    const setupIntentId = setupIntent?.id || paymentIntent?.id;
    if (status === 'requires_action') {
      const newResult = await stripe?.handleCardAction(props.clientSecret);
      if (newResult) handleSetupIntentResult(newResult);
    } else if (status === 'succeeded') {
      const paymentInfo: StripePaymentInformation = {
        billing_info: billingInfo as StripeCustomerBillingInfo,
        setup_intent: setupIntentId ?? '',
      };

      const updatedInfo: StripeBusinessInfo = {
        company_info: {
          name: businessName ?? '',
        },
        invoice_email: email ?? '',
        is_business: isBusiness,
      };

      if (taxId && taxIdInfo?.countryEnum) {
        updatedInfo.tax_id = {
          value: taxId,
          type: taxIdInfo?.countryEnum,
        };
      }
      try {
        await onPaymentMethodSuccess({
          paymentInfo,
          updatedInfo,
          isBusiness: !!isBusiness,
        });
        setLoading(false);
        props.onNext?.();
      } catch (error) {
        setLoading(false);
        handleError(error as Error);
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isBusiness) {
      if (taxIdInfo && taxId.length > 0) {
        const regex = new RegExp(taxIdInfo.regex, 'g');
        const result = regex.test(taxId);
        if (!result) {
          setTaxIdError(
            `${taxIdInfo.taxIdName} is incorrect. If this business doesn't have one, please clear this field`
          );
          return;
        }
      }

      if (!validateEmail(businessFormProps.email)) {
        setEmailError('Invalid invoice email');
        return;
      }
    }

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      handleError(submitError);
      return;
    }

    // Stripe might redirect here which won't continue with the payment flow
    const result = await stripe.confirmSetup({
      elements,
      clientSecret: props.clientSecret,
      confirmParams: {
        return_url: redirectUrl,
      },
      redirect: 'if_required',
    });

    handleSetupIntentResult(result);
  };

  const submitDisabled =
    !stripe ||
    !elements ||
    loading ||
    !addressComplete ||
    !paymentComplete ||
    props.loading;

  const submitButton = (
    <Button
      type="submit"
      disabled={submitDisabled}
      loading={loading || props.loading}
      size="md"
    >
      Add and Continue
    </Button>
  );
  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <PaymentElement
        onChange={(event) => {
          setPaymentComplete(event.complete);
        }}
      />
      {!isPersonal && (
        <Checkbox
          checked={isBusiness}
          onChange={(e) => {
            const checked = e.currentTarget.checked;
            setIsBusiness(checked);
          }}
          label={
            <>
              <div>This is a business</div>
              <div className="text-sm text-tertiary">
                Select to edit business information and tax id
              </div>
            </>
          }
        ></Checkbox>
      )}
      <BusinessInfoSubForm {...businessFormProps} />
      <div className="flex gap-3 self-end">
        {props.onPrev && (
          <Button variant="outlined" onClick={props.onPrev}>
            Back
          </Button>
        )}
        {submitDisabled ? (
          <Tooltip title="Please complete all fields">
            <div>{submitButton}</div>
          </Tooltip>
        ) : (
          submitButton
        )}
      </div>
      {errorMessage && <div>{errorMessage}</div>}
    </form>
  );
}
